'use client'

import { observeClientOperation } from '@brew/observability/browser'
import type { MetricOptions } from '@brew/observability/metrics'
import { classifyProductMutationResult } from './product-result'

/** Request completion is distinct from browser navigation or a background job. */
const REQUEST_PHASES = {
  'billing.checkout_session': 'session_ready',
  'billing.portal_session': 'session_ready',
  'billing.checkout_verify': 'verified',
  'billing.change_plan': 'persisted',
  'billing.cancel': 'persisted',
  'billing.reactivate': 'persisted',
  'billing.redeem': 'persisted',
  'onboarding.profile': 'persisted',
  'onboarding.referral': 'persisted',
  'onboarding.brand_ack': 'persisted',
  'onboarding.stack': 'persisted',
  'onboarding.tools': 'persisted',
  'onboarding.taste': 'persisted',
  'onboarding.api_key': 'credential_ready',
  'onboarding.template_match': 'response',
  'onboarding.extraction': 'accepted',
  'onboarding.domain_finalize': 'response',
} as const

/** Finite request catalog; payloads, secret material and human answers never enter telemetry. */
export function observeProductRequest<T>(
  request: keyof typeof REQUEST_PHASES,
  callback: () => Promise<T>,
  options: {
    context?: MetricOptions['context']
    resource?: 'free' | 'starter' | 'growth' | 'pro' | 'enterprise'
    trigger?: 'monthly' | 'commit' | 'skip' | 'continue'
  } = {}
): Promise<T> {
  const domain = request.startsWith('billing.') ? 'billing' : 'onboarding'
  return observeClientOperation(
    `client.${request}`,
    {
      tags: {
        domain,
        surface: domain,
        phase: REQUEST_PHASES[request],
        transport:
          request === 'onboarding.extraction' ||
          request === 'onboarding.domain_finalize'
            ? 'http'
            : 'server_action',
        ...(options.resource ? { resource: options.resource } : {}),
        ...(options.trigger ? { trigger: options.trigger } : {}),
      },
      context: options.context,
      classifyResult: (result) => {
        if (
          request === 'onboarding.template_match' &&
          result &&
          typeof result === 'object' &&
          'status' in result &&
          result.status === 'unavailable'
        ) {
          return 'partial'
        }
        return classifyProductMutationResult(result)
      },
    },
    callback
  )
}
